import { useAuth } from '@/auth/AuthContext'
import { LoadingScreen } from '@/components/LoadingScreen'
import { getLocale, toIntlLocale } from '@/config/locale'
import { getDisplayLabel, useCurrentUser } from '@/features/user/api'
import { translate, useTranslation } from '@/i18n'
import { Link } from 'react-router-dom'

export function ProfilePage() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const locale = getLocale()
  const currentUserQuery = useCurrentUser(Boolean(user))

  if (currentUserQuery.isLoading) return <LoadingScreen />

  if (currentUserQuery.isError || !currentUserQuery.data) {
    return (
      <section className="rounded-[1.35rem] border border-red-900/60 bg-red-950/30 p-5">
        <h1 className="text-xl font-black tracking-tight text-white">{t('Profiel')}</h1>
        <p className="mt-2 text-sm text-red-100/85">
          {t('Je profiel kon niet worden geladen.')}
        </p>
      </section>
    )
  }

  const profile = currentUserQuery.data
  const displayName = getDisplayLabel(profile)
  const favoriteTeam =
    profile.favorite_team?.display_name?.trim() ||
    profile.favorite_team?.name?.trim() ||
    t('Nog niet ingesteld')
  const rankName = profile.rank?.name?.trim() || t('Nog geen rank')

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-400">
          {t('Profiel')}
        </p>
        <h1 className="mt-3 text-2xl font-black tracking-tight text-white">
          {displayName}
        </h1>
        <p className="mt-2 text-sm text-surface-400">
          {t('Je account, punten en voorkeuren op één plek.')}
        </p>
      </div>

      <section className="grid gap-6 rounded-[1.5rem] border border-white/[0.06] bg-white/[0.02] p-5 sm:p-6 md:grid-cols-[1.05fr_0.95fr] md:divide-x md:divide-white/[0.06]">
        <div className="md:pr-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
            {t('Account')}
          </p>
          <div className="mt-4 space-y-3">
            <ProfileRow label={t('Naam')} value={displayName} />
            <ProfileRow label={t('E-mail')} value={profile.email?.trim() || t('Onbekend')} />
            <ProfileRow label={t('Favoriete team')} value={favoriteTeam} />
            <ProfileRow
              label={t('Laatste login')}
              value={formatOptionalDateTime(profile.last_login_at, locale)}
            />
          </div>
        </div>

        <div className="space-y-5 md:pl-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
              {t('Score')}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <StatTile
                label={t('Totaal punten')}
                value={formatNumber(profile.user_points_dashboard?.total_points)}
              />
              <StatTile
                label={t('Actieve poules')}
                value={formatNumber(profile.user_points_dashboard?.active_poules)}
              />
              <StatTile label={t('Rank score')} value={formatNumber(profile.rank_score)} />
              <StatTile
                label={t('Profiel')}
                value={profile.is_profile_complete ? t('Compleet') : t('Open')}
              />
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
              {t('Rank')}
            </p>
            <div className="mt-3 rounded-[1rem] bg-white/[0.03] px-4 py-4">
              <p className="text-lg font-black text-white">{rankName}</p>
              <p className="mt-1 text-sm text-surface-400">
                {profile.rank?.color?.trim() || t('Geen kleur ingesteld')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Link
        to="/profiel/prijzenkast"
        className="group block rounded-[1.35rem] border border-white/[0.07] bg-[#0d1113] p-5 transition hover:border-accent-400/30 hover:bg-[#101719] sm:p-6"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-300">{t('Prijzenkast')}</p>
            <h2 className="mt-2 text-xl font-black tracking-tight text-white">{t('Verdiende trofeeën')}</h2>
            <p className="mt-2 text-sm leading-6 text-surface-400">{t('Bekijk je prijzen en persoonlijke records per spel.')}</p>
          </div>
          <span className="shrink-0 text-2xl text-surface-400 transition group-hover:translate-x-1 group-hover:text-white" aria-hidden="true">→</span>
        </div>
      </Link>
    </div>
  )
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[0.95rem] bg-white/[0.03] px-4 py-3">
      <span className="text-sm text-surface-400">{label}</span>
      <span className="text-sm font-semibold text-white">{value}</span>
    </div>
  )
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[0.95rem] bg-white/[0.03] px-4 py-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
        {label}
      </p>
      <p className="mt-2 text-lg font-black text-white">{value}</p>
    </div>
  )
}

function formatNumber(value?: number) {
  return typeof value === 'number' ? String(value) : '0'
}

function formatOptionalDateTime(value?: string | null, locale?: string) {
  if (!value) return translate('Onbekend')

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}
